import type { EventInput } from '@fullcalendar/core';
import type { Absence } from '@src/types';
import {
  daysRange,
  formatDate,
  formatDateRange,
  formatIsoDate,
  isSameDate,
} from '@src/utils';
import type { AbsenceSelectionData, TimelineSlot } from '../types';
import { userResourceId } from './resources';

function toCalendarDate(value: Date | string): Date {
  if (value instanceof Date) {
    return value;
  }

  const isoDate = formatIsoDate(value);
  return new Date(`${isoDate}T00:00:00`);
}

export function getEventLabel(
  start: Date | string,
  end: Date | string,
  dayView = false,
) {
  const startDate = toCalendarDate(start);
  const endDate = toCalendarDate(end);

  if (!dayView) return startDate.getDate();
  if (isSameDate(startDate, endDate)) {
    return formatDate(startDate, {
      day: '2-digit',
      month: '2-digit',
    });
  } else {
    return formatDateRange(startDate, endDate, {
      day: '2-digit',
      month: '2-digit',
    });
  }
}
// XXX:function to replace absence Duration, check if we need this
// function eventDurationFromAbsence(absence: Absence) {
//     let duration = {};
// }
//
// TODO: check if we need slot here or just selected ids?
// export function absencesToEvents(slot, absences, dayView = false) {
export function absencesToEvents(
  absences: Absence[],
  dayView: boolean = false,
): EventInput[] {
  if (!absences?.length) {
    return [];
  }

  // TODO: check if we still need this?
  // function absenceDuration(item) {
  //   let duration = {
  //     start: item.date,
  //     end: item.date,
  //     allDay: item.allDay,
  //   };
  //   if (item.startTime) {
  //     duration = {
  //       ...duration,
  //       start: formatDateTime(item.date, item.startTime),
  //     };
  //   }
  //   if (item.acti_end_time) {
  //     duration = {
  //       ...duration,
  //       end: formatDateTime(item.date, item.endTime),
  //     };
  //   }
  // TODO: check why we need this?
  // const slotDuration = getSelectedAbsenceDuration(item, slot);
  // if (slotDuration) {
  //   duration = {
  //     ...duration,
  //     ...slotDuration,
  //   };
  // }
  // return duration;
  // }
  return absences.map((a) => {
    // const eventData = getAbsenceDataById(a.id, absences);
    return {
      id: String(a.id),
      resourceId: userResourceId(a.userId),
      type: a.typeId,
      start: formatIsoDate(a.date, a.startTime ?? undefined),
      end: formatIsoDate(a.date, a.endTime ?? undefined),
      allDay: a.allDay,
      color: a.colour,
      block_position: a.blockPosition,
      workday_count: a.workdays,
      show_workday_count: a.showWorkdays,
      editalbe: false,
      // ...eventDurationFromAbsence(a),
      // ...absenceDuration(a),
      ...(dayView && {
        resourceEditable: false,
        startEditable: false,
        durationEditable: true,
        //   extendedProps: {
        //     label: getEventLabel(eventData.date_from, eventData.date_to, true),
        //   },
      }),
    };
  });
}

export function absenceSelectionToSlot(
  data: AbsenceSelectionData,
): TimelineSlot {
  return {
    start: data.start,
    end: data.end,
    startTime: data.startTime,
    endTime: data.endTime,
    selectedIds: data.absenceIds,
    resourceId: userResourceId(data.userId),
    userId: data.userId,
  };
}

export function getUserAbsences(userId: number, absences: Absence[]) {
  if (!userId || !absences || !absences.length) {
    return [];
  }
  return absences.filter((absence) => absence.userId === userId);
}

type AbsencePayloadInput = {
  startTime?: string | null;
  endTime?: string | null;
  dateFrom?: Date | string | null;
  dateTo?: Date | string | null;
  [key: string]: unknown;
};

export function prepareAbsencePayload(values: AbsencePayloadInput) {
  const { dateFrom, dateTo, startTime, endTime } = values;
  return {
    acti_user: values.userId,
    date_from: dateFrom ? formatIsoDate(dateFrom) : null,
    date_to: dateTo ? formatIsoDate(dateTo) : null,
    ...(startTime ? { acti_start_time: startTime } : {}),
    ...(endTime ? { acti_end_time: endTime } : {}),
    acti_type: values.typeId,
    acti_tentative: values.tentative,
    acab_notes: values.notes,
  };
}
export function getAbsencesIdsForRange(
  dateFrom: Date,
  dateTo: Date,
  absences: Absence[],
): number[] {
  const range = daysRange(dateFrom, dateTo).map((d) => formatIsoDate(d));
  return absences.filter((a) => range.includes(a.dateStr)).map((a) => a.id);
}

export function getEventTimeDelta(info: { milliseconds: number }) {
  if (info.milliseconds !== 0) {
    return info.milliseconds / 1000 / 60;
  }
  return 0;
}

export function shiftTime(time: string, deltaMinutes: number) {
  const [h, m] = time.split(':').map(Number);
  const d = new Date(1970, 0, 1, h, m, 0, 0);
  d.setMinutes(d.getMinutes() + deltaMinutes);
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

// function getSelectedAbsenceDuration(absence, slot) {
//   if (
//     !slot ||
//     slot.absences_ids?.length !== 1 ||
//     slot.absences_ids[0] !== absence.id
//   ) {
//     return null;
//   }
//   if (!slot.startTime && !slot.endTime) {
//     return null;
//   }
//
//   return {
//     allDay: false,
//     ...(slot.startTime && {
//       start: formatDateTime(slot.date_from, slot.startTime),
//     }),
//     ...(slot.endTime && { end: formatDateTime(slot.date_from, slot.endTime) }),
//   };
// }
